import { type TouchEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useMatch, useNavigate } from "react-router";
import { rememberPageOnExit, takeLeftPage, useOpenCharacterActions, useOpenCharacters } from "../api/queries";
import type { OpenCharacterView } from "../api/types";
import { text } from "../text";
import { Sheet } from "./Sheet";

/** The page an open character returns to; a remembered page of the other mode falls back to the character's start page. */
export function characterPath(character: OpenCharacterView): string {
  const base = `/c/${character.id}`;
  const last = character.lastPath;
  if (!last) return base;
  const otherMode = character.mode === "career" ? "assistent" : "spiel";
  return last === otherMode || last.startsWith(`${otherMode}/`) ? base : `${base}/${last}`;
}

function useActiveCharacterId(): string | undefined {
  return useMatch("/c/:id/*")?.params.id;
}

function pageOf(id: string, pathname: string): string {
  const prefix = `/c/${id}/`;
  return pathname.startsWith(prefix) ? pathname.slice(prefix.length).replace(/\/+$/, "") : "";
}

/**
 * Adds the shown character to the open characters and remembers the page shown for it.
 * The page is remembered after it has rendered; when the browser leaves the app before the server has confirmed it,
 * e.g. on a reload right after switching pages, it is sent once more on the way out. Leaving may abort the request under
 * way and the ones waiting behind it even before the page is told, so what counts is the confirmation, not what is pending.
 * Off (`enabled` false) for a character the user may only read.
 */
export function useTrackOpenCharacter(id: string, enabled = true) {
  const { pathname } = useLocation();
  const { open } = useOpenCharacterActions();
  const lastPath = enabled ? pageOf(id, pathname) : "";
  const confirmed = useRef("");
  const { mutate } = open;
  useEffect(() => {
    if (!lastPath) return;
    confirmed.current = "";
    mutate({ id, lastPath }, { onSuccess: () => (confirmed.current = lastPath) });
  }, [id, lastPath, mutate]);
  useEffect(() => {
    if (!enabled) return;
    const leave = () => {
      const current = pageOf(id, window.location.pathname);
      if (current && current !== confirmed.current) rememberPageOnExit(id, current);
    };
    window.addEventListener("pagehide", leave);
    return () => window.removeEventListener("pagehide", leave);
  }, [id, enabled]);
}

/**
 * Sends the page remembered on leaving once more after the app has loaded again, in order behind the other changes: the
 * first list read on loading may predate it, and an older request still running may have overwritten it. When the app
 * loads on the same character, the page shown now is remembered instead.
 */
export function useResendLeftPage() {
  const { mutate } = useOpenCharacterActions().open;
  useEffect(() => {
    const left = takeLeftPage();
    if (left && !`${window.location.pathname}/`.startsWith(`/c/${left.id}/`)) mutate(left);
  }, [mutate]);
}

const scrollStorageKey = "splittertraeger.scroll";

interface ScrollMark {
  path: string;
  y: number;
}

/** The scroll position last seen per character and page; kept in this browser tab only, across reloads. */
const scrollMarks = new Map<string, ScrollMark>(readScrollMarks());
let scrollSave = 0;

function readScrollMarks(): [string, ScrollMark][] {
  try {
    return Object.entries(JSON.parse(sessionStorage.getItem(scrollStorageKey) ?? "{}") as Record<string, ScrollMark>);
  } catch {
    return [];
  }
}

function saveScrollMarks() {
  window.clearTimeout(scrollSave);
  try {
    sessionStorage.setItem(scrollStorageKey, JSON.stringify(Object.fromEntries(scrollMarks)));
  } catch {
    // Without storage the positions last until the next reload.
  }
}

function markScroll(id: string, mark: ScrollMark) {
  scrollMarks.set(id, mark);
  window.clearTimeout(scrollSave);
  scrollSave = window.setTimeout(saveScrollMarks, 250);
}

window.addEventListener("pagehide", saveScrollMarks);

/**
 * Scrolls to the given position, trying again while the page is still filling; the user's own scrolling ends it.
 * Scroll anchoring is off meanwhile: content that grows above the position later would otherwise move it.
 */
function restoreScroll(y: number): () => void {
  const until = performance.now() + 1500;
  const root = document.documentElement.style;
  root.overflowAnchor = "none";
  let frame = 0;
  let timer = 0;
  const end = () => {
    window.clearTimeout(timer);
    root.overflowAnchor = "";
    stop();
  };
  const stop = () => {
    cancelAnimationFrame(frame);
    window.removeEventListener("wheel", end);
    window.removeEventListener("touchstart", end);
  };
  const step = () => {
    window.scrollTo(0, y);
    if (Math.abs(window.scrollY - y) > 1 && performance.now() < until) frame = requestAnimationFrame(step);
    else stop();
  };
  window.addEventListener("wheel", end, { passive: true });
  window.addEventListener("touchstart", end, { passive: true });
  timer = window.setTimeout(end, until - performance.now());
  step();
  return end;
}

/**
 * Remembers how far each character's page was scrolled and returns there when switching back to the character;
 * a character without a position for the page it opens on starts at the top.
 */
export function useCharacterScroll(id: string) {
  const { pathname } = useLocation();
  const path = pageOf(id, pathname);
  const shown = useRef({ id, path });

  useLayoutEffect(() => {
    const mark = scrollMarks.get(id);
    if (mark && mark.path === pageOf(id, window.location.pathname)) return restoreScroll(mark.y);
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    // A page change within the character keeps the scroll position the browser shows for the new page.
    if (shown.current.id === id && shown.current.path !== path && path) markScroll(id, { path, y: Math.round(window.scrollY) });
    shown.current = { id, path };
  }, [id, path]);

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const current = pageOf(id, window.location.pathname);
        if (current) markScroll(id, { path: current, y: Math.round(window.scrollY) });
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
    };
  }, [id]);
}

/** Whether a touch on the element belongs to something that handles it itself: a field, a row of tabs, or an area that scrolls sideways. */
function keepsTouch(target: Element, boundary: Element): boolean {
  if (target.closest("input, textarea, select, [contenteditable], [data-own-swipe]")) return true;
  for (let element: Element | null = target; element && element !== boundary; element = element.parentElement) {
    if (element.scrollWidth > element.clientWidth + 1 && /auto|scroll/.test(getComputedStyle(element).overflowX)) return true;
  }
  return false;
}

/**
 * Touch handlers for the shown character: a quick horizontal swipe moves to the next open character (to the left)
 * or the previous one (to the right). Swipes on fields, on areas that scroll sideways and in dialogs are left alone.
 */
export function useSwipeToNeighbour(id: string) {
  const list = useOpenCharacters().data ?? [];
  const navigate = useNavigate();
  const start = useRef<{ x: number; y: number; time: number } | null>(null);
  return {
    onTouchStart: (e: TouchEvent<HTMLElement>) => {
      const target = e.target as Element;
      const touch = e.touches.length === 1 ? e.touches[0] : undefined;
      const own = e.currentTarget.contains(target) && !keepsTouch(target, e.currentTarget);
      start.current = own && touch ? { x: touch.clientX, y: touch.clientY, time: e.timeStamp } : null;
    },
    onTouchEnd: (e: TouchEvent<HTMLElement>) => {
      const from = start.current;
      start.current = null;
      const touch = e.changedTouches[0];
      if (!from || !touch || e.touches.length > 0) return;
      const dx = touch.clientX - from.x;
      const dy = touch.clientY - from.y;
      if (Math.abs(dx) < Math.max(60, window.innerWidth / 4) || Math.abs(dy) > Math.abs(dx) / 2 || e.timeStamp - from.time > 800) return;
      const index = list.findIndex((c) => c.id === id);
      const neighbour = index < 0 ? undefined : list[dx < 0 ? index + 1 : index - 1];
      if (neighbour) navigate(characterPath(neighbour));
    },
    onTouchCancel: () => {
      start.current = null;
    },
  };
}

/**
 * Moving an open character one place forward or back without dragging. The new place is announced in a live region,
 * and the focus follows the moved character once the list shows the new order.
 */
function useMoveCharacter(list: OpenCharacterView[], focusTarget: (id: string, offset: number) => HTMLElement | null | undefined) {
  const { reorder } = useOpenCharacterActions();
  const [announcement, setAnnouncement] = useState("");
  const pending = useRef<{ id: string; offset: number; order: string } | null>(null);
  const order = list.map((c) => c.id).join();

  useLayoutEffect(() => {
    const moved = pending.current;
    if (!moved || moved.order !== order) return;
    pending.current = null;
    const target = focusTarget(moved.id, moved.offset);
    if (target && document.activeElement !== target) target.focus();
  });

  const move = (id: string, offset: number) => {
    const ids = list.map((c) => c.id);
    const from = ids.indexOf(id);
    const to = from + offset;
    const character = list[from];
    if (!character || to < 0 || to >= ids.length) return;
    ids.splice(from, 1);
    ids.splice(to, 0, id);
    pending.current = { id, offset, order: ids.join() };
    reorder.mutate(ids);
    setAnnouncement(`${character.name} an Stelle ${to + 1} von ${ids.length}`);
  };
  return { move, announcement };
}

/** Closes an open character; closing the shown one moves on to its neighbour, or to the character list. */
function useCloseCharacter(list: OpenCharacterView[]) {
  const active = useActiveCharacterId();
  const navigate = useNavigate();
  const { close } = useOpenCharacterActions();
  return (id: string) => {
    if (id === active) {
      const index = list.findIndex((c) => c.id === id);
      const neighbour = list[index + 1] ?? list[index - 1];
      navigate(neighbour ? characterPath(neighbour) : "/");
    }
    close.mutate(id);
  };
}

/**
 * Desktop: one tab per open character below the top bar, with close button, and a link to the group page.
 * Tabs are reordered by dragging, or from the keyboard with Ctrl+Shift+arrow keys on the focused tab.
 */
export function OpenCharacterTabs() {
  const list = useOpenCharacters().data ?? [];
  const active = useActiveCharacterId();
  const closeCharacter = useCloseCharacter(list);
  const { reorder } = useOpenCharacterActions();
  const [dragged, setDragged] = useState<string | null>(null);
  const nav = useRef<HTMLElement>(null);
  const { move, announcement } = useMoveCharacter(list, (id) => nav.current?.querySelector<HTMLElement>(`[data-character="${id}"] a`));
  if (list.length === 0) return null;

  const drop = (target: string) => {
    if (!dragged || dragged === target) return;
    const ids = list.map((c) => c.id).filter((id) => id !== dragged);
    const from = list.findIndex((c) => c.id === dragged);
    const to = list.findIndex((c) => c.id === target);
    ids.splice(from < to ? ids.indexOf(target) + 1 : ids.indexOf(target), 0, dragged);
    reorder.mutate(ids);
  };

  return (
    <nav className="open-tabs" aria-label="Offene Charaktere" ref={nav}>
      <div className="open-tabs-inner">
        <ul>
          {list.map((c) => (
            <li
              key={c.id}
              className={`open-tab${c.id === active ? " active" : ""}${c.id === dragged ? " dragging" : ""}`}
              data-character={c.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", c.name);
                setDragged(c.id);
              }}
              onDragOver={(e) => {
                if (dragged) e.preventDefault();
              }}
              onDrop={(e) => {
                e.preventDefault();
                drop(c.id);
                setDragged(null);
              }}
              onDragEnd={() => setDragged(null)}
            >
              <Link
                to={characterPath(c)}
                draggable={false}
                aria-current={c.id === active ? "page" : undefined}
                aria-keyshortcuts="Control+Shift+ArrowLeft Control+Shift+ArrowRight"
                title="Strg+Umschalt+Pfeiltaste verschiebt den Tab"
                onKeyDown={(e) => {
                  if (!e.ctrlKey || !e.shiftKey || (e.key !== "ArrowLeft" && e.key !== "ArrowRight")) return;
                  e.preventDefault();
                  move(c.id, e.key === "ArrowLeft" ? -1 : 1);
                }}
              >
                <span className="open-tab-name">{c.name}</span>
                <span className="open-tab-system">{text(c.systemName)}</span>
              </Link>
              <button type="button" className="open-tab-close" aria-label={`${c.name} schließen`} title="Schließen" onClick={() => closeCharacter(c.id)}>
                ×
              </button>
            </li>
          ))}
        </ul>
        <NavLink to="/gruppe" className="open-group" title="Alle offenen Charaktere nebeneinander">
          Gruppe
        </NavLink>
      </div>
      <p className="visually-hidden" aria-live="polite">
        {announcement}
      </p>
    </nav>
  );
}

/**
 * Phone: the name of the shown character in the top bar opens the list of open characters.
 * The list is rendered into the body so it neither inherits the colours nor the stacking of the top bar.
 */
export function OpenCharacterSwitcher() {
  const list = useOpenCharacters().data ?? [];
  const active = useActiveCharacterId();
  const closeCharacter = useCloseCharacter(list);
  const [shown, setShown] = useState(false);
  const sheetList = useRef<HTMLUListElement>(null);
  const { move, announcement } = useMoveCharacter(list, (id, offset) => {
    const buttons = sheetList.current?.querySelector(`[data-character="${id}"]`)?.querySelectorAll<HTMLButtonElement>("button[data-move]");
    const [forward, back] = [buttons?.[0], buttons?.[1]];
    const preferred = offset < 0 ? forward : back;
    return preferred?.disabled ? (offset < 0 ? back : forward) : preferred;
  });
  const { pathname } = useLocation();
  useEffect(() => setShown(false), [pathname]);
  const current = list.find((c) => c.id === active);
  if (list.length === 0) return null;

  return (
    <>
      <button type="button" className="open-switcher" aria-haspopup="dialog" aria-expanded={shown} onClick={() => setShown(true)}>
        <span className="open-switcher-name">{current ? current.name : `${list.length} offen`}</span>
        <span aria-hidden="true">▾</span>
      </button>
      {shown &&
        createPortal(
          <Sheet
            title="Offene Charaktere"
            onClose={() => setShown(false)}
            footer={
              <>
                <Link to="/gruppe" className="button" onClick={() => setShown(false)}>
                  Gruppe
                </Link>
                <Link to="/" className="button" onClick={() => setShown(false)}>
                  Alle Charaktere
                </Link>
              </>
            }
          >
            <ul className="open-list" ref={sheetList}>
              {list.map((c, index) => (
                <li key={c.id} className={c.id === active ? "active" : undefined} data-character={c.id}>
                  <Link to={characterPath(c)} aria-current={c.id === active ? "page" : undefined} onClick={() => setShown(false)}>
                    <strong>{c.name}</strong>
                    <span className="muted small">
                      {text(c.systemName)} · {c.mode === "creation" ? "Erschaffungsmodus" : "Karrieremodus"}
                    </span>
                  </Link>
                  <button
                    type="button"
                    className="icon-button"
                    data-move="-1"
                    aria-label={`${c.name} nach vorn verschieben`}
                    disabled={index === 0}
                    onClick={() => move(c.id, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    data-move="1"
                    aria-label={`${c.name} nach hinten verschieben`}
                    disabled={index === list.length - 1}
                    onClick={() => move(c.id, 1)}
                  >
                    ↓
                  </button>
                  <button type="button" className="icon-button" aria-label={`${c.name} schließen`} onClick={() => closeCharacter(c.id)}>
                    ×
                  </button>
                </li>
              ))}
            </ul>
            <p className="visually-hidden" aria-live="polite">
              {announcement}
            </p>
          </Sheet>,
          document.body,
        )}
    </>
  );
}
